import React, { useRef } from 'react';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import { X } from 'lucide-react';
import { Dialog, DialogPortal, DialogOverlay, DialogTitle, DialogDescription, DialogClose } from '../ui/dialog';

export const PRIVACY_STATEMENT_VERSION = '2026-10-08';
export const PRIVACY_STATEMENT_EFFECTIVE_DATE = 'October 8, 2026';

export const PrivacyStatementContent = ({ scrollable = false }) => (
  <div className={`${scrollable ? 'min-h-0 overflow-y-auto' : ''} px-6 py-5 text-sm leading-7 text-white/75`} data-testid="privacy-modal-content">
    <div className="space-y-6">
      <section>
        <h3 className="text-base font-semibold text-white">1. Who we are</h3>
        <p className="mt-2">
          MyGoal App B.V. provides the MyGoal platform for athletes, coaches, clubs, and administrators.
          If you have privacy questions or want to exercise your rights, you can contact us at{' '}
          <a href="mailto:privacy@mygoalapp.com" className="text-[#92a7e8] hover:underline">
            privacy@mygoalapp.com
          </a>.
        </p>
      </section>

      <section>
        <h3 className="text-base font-semibold text-white">2. What data we collect</h3>
        <p className="mt-2">
          We may collect profile details such as your name, email address, account type, club or coach links,
          training sessions, ratings, optional named player–coach messages, goals, attendance choices, agenda items, notifications, and basic usage
          information needed to keep the app secure and working properly.
        </p>
      </section>

      <section>
        <h3 className="text-base font-semibold text-white">3. Why we use your data</h3>
        <p className="mt-2">
          We use personal data to create and manage accounts, show training and goal progress, connect athletes
          with coaches and clubs, deliver private conversations, support attendance and agenda flows, provide analytics, send reminders, and
          improve the platform experience.
        </p>
      </section>

      <section>
        <h3 className="text-base font-semibold text-white">4. Who can see your data</h3>
        <p className="mt-2">
          Athletes can view their own profile, sessions, goals, and agenda data. Coaches can view information
          related to athletes and groups connected to them, but they only see <span className="font-semibold text-white">aggregated team ratings</span>, never
          individual rating scores tied to a specific athlete by name. Club administrators can view organization-level
          data made available through the platform. Internal admin access is limited to support, security, and account
          management purposes. Optional activity messages are not anonymous: they show the sender’s name and activity name to the chosen coach, without including rating scores. Private chats are available only to the two participants while their connection is active, not to other coaches or club accounts.
        </p>
      </section>

      <section>
        <h3 className="text-base font-semibold text-white">5. Notifications and reminders</h3>
        <p className="mt-2">
          If you allow notifications, MyGoal may send reminders about scheduled activities and rating follow-ups.
          On some devices these reminders are handled locally on the device before a full server-sent push setup is enabled.
        </p>
      </section>

      <section>
        <h3 className="text-base font-semibold text-white">6. AI features</h3>
        <p className="mt-2">
          Coach-facing AI features may use team and training context to generate advice. The data sent to the
          AI provider is <span className="font-semibold text-white">anonymized</span> — athlete and coach names are never shared with the AI, and the
          AI is explicitly instructed not to reference any personal identifiers in its output. AI advice is meant
          to support coaching decisions and should not replace human judgment. Private chat messages are not included in AI prompts or team analytics.
        </p>
      </section>

      <section>
        <h3 className="text-base font-semibold text-white">7. How long we keep data</h3>
        <p className="mt-2">
          We keep data for as long as it is needed to provide the service, meet legal obligations, resolve disputes,
          and maintain platform security. Chat messages expire after one year and can be deleted earlier by their sender. Requesting account deletion removes the account’s conversations from the live chat store immediately. Some records may remain in backups for a limited time.
        </p>
      </section>

      <section>
        <h3 className="text-base font-semibold text-white">8. Your rights</h3>
        <p className="mt-2">
          Depending on your location, you may have rights to access, correct, delete, restrict, or export your data,
          and to object to certain processing. In Chat you can edit or delete your own messages, delete all your sent messages, and export your contributions, including after disconnecting. For other requests, contact{' '}
          <a href="mailto:privacy@mygoalapp.com" className="text-[#92a7e8] hover:underline">
            privacy@mygoalapp.com
          </a>.
        </p>
      </section>

      <section>
        <h3 className="text-base font-semibold text-white">9. Security</h3>
        <p className="mt-2">
          We use reasonable technical and organizational measures to protect personal data, but no system can be
          guaranteed to be completely secure.
        </p>
      </section>

      <section>
        <h3 className="text-base font-semibold text-white">10. Changes</h3>
        <p className="mt-2">
          We may update this privacy statement from time to time. When we do, we will update the effective date on this page.
        </p>
      </section>
    </div>
  </div>
);

export const PrivacyStatementModal = ({ open, onClose }) => {
  const opener = useRef(null);
  return (
    <Dialog open={open} onOpenChange={value => { if (!value) onClose(); }}>
      <DialogPortal>
        <DialogOverlay data-testid="privacy-modal-overlay" className="bg-black/70" />
        <DialogPrimitive.Content onOpenAutoFocus={() => { opener.current = document.activeElement; }} onCloseAutoFocus={event => { event.preventDefault(); if (opener.current?.isConnected) opener.current.focus(); }} data-testid="privacy-modal" className="fixed left-1/2 top-1/2 z-50 flex max-h-[85vh] w-[calc(100vw-2rem)] max-w-3xl -translate-x-1/2 -translate-y-1/2 flex-col overflow-hidden rounded-[32px] border border-white/10 bg-[#11121b] shadow-2xl">
          <div className="flex shrink-0 items-start justify-between gap-4 border-b border-white/10 px-6 py-5">
            <div>
              <p className="text-xs uppercase tracking-[0.28em] text-white/40">Privacy</p>
              <DialogTitle className="mt-2 text-2xl font-bold text-white">Privacy Statement</DialogTitle>
              <DialogDescription className="mt-2 text-sm text-white/55">MyGoal App B.V. — Effective date: {PRIVACY_STATEMENT_EFFECTIVE_DATE}</DialogDescription>
            </div>
            <DialogClose asChild>
              <button type="button" aria-label="Close privacy statement" data-testid="privacy-modal-close-button" className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-white/5 text-white/60 hover:bg-white/10 hover:text-white">
                <X className="h-5 w-5" />
              </button>
            </DialogClose>
          </div>
          <PrivacyStatementContent scrollable />
        </DialogPrimitive.Content>
      </DialogPortal>
    </Dialog>
  );
};
